import React, { useEffect, useRef, useState, useCallback } from "react";
import CabIcon from "./CabIcon";
import { putProfileAbout } from "../../api/cabinet";

/**
 * «О себе» с оформлением: рендерер + полноценный мини-редактор.
 *
 * Формат хранения — НЕ HTML (XSS), а JSON прогонов текста:
 *   {v:1, paras:[{align?, runs:[{t, b?, i?, u?, s?, c?, sz?, f?}]}]}
 * Схема и диапазоны согласованы с whitelist бэка (_clean_about_rich в
 * routs/Profile.py): b/i/u/s — жирный/курсив/подчёркнутый/зачёркнутый,
 * c — цвет #rrggbb, sz — размер 1..7 (3 = базовый), f — ключ шрифта.
 *
 * Редактор — contenteditable + document.execCommand (работает во всех
 * браузерах); при сохранении DOM разбирается в JSON, при загрузке JSON
 * собирается обратно в разметку. Лимит 600 символов — по чистому тексту.
 */

export const ABOUT_MAX_LEN = 600;

const SIZE_PX = { 1: 11, 2: 13, 3: 15, 4: 18, 5: 22, 6: 27, 7: 32 };
const SIZE_TITLES = { 1: "Мелкий", 2: "Небольшой", 3: "Обычный", 4: "Крупный", 5: "Большой", 6: "Огромный", 7: "Гигантский" };

const FONT_STACKS = {
  default: "",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "Consolas, 'Courier New', monospace",
  rounded: "'Comic Sans MS', 'Comic Sans', cursive",
  hand: "'Segoe Script', 'Bradley Hand', cursive",
};
const FONT_TITLES = {
  default: "Обычный",
  serif: "С засечками",
  mono: "Моноширинный",
  rounded: "Округлый",
  hand: "Рукописный",
};

const PALETTE = [
  "#0a0e1a", "#64748b", "#e11d48", "#f97316", "#f59e0b", "#16a34a",
  "#06b6d4", "#3357ff", "#6d28d9", "#ec4899", "#78350f", "#94a3b8",
];

/* ── общее: inline-стиль одного прогона ────────────────────────── */
function runStyle(run) {
  const st = {};
  if (run.b) st.fontWeight = 800;
  if (run.i) st.fontStyle = "italic";
  const dec = [];
  if (run.u) dec.push("underline");
  if (run.s) dec.push("line-through");
  if (dec.length) st.textDecoration = dec.join(" ");
  if (run.c) st.color = run.c;
  if (run.sz && SIZE_PX[run.sz]) st.fontSize = `${SIZE_PX[run.sz]}px`;
  if (run.f && FONT_STACKS[run.f]) st.fontFamily = FONT_STACKS[run.f];
  return st;
}

function parseRich(richStr) {
  if (!richStr) return null;
  try {
    const o = JSON.parse(richStr);
    if (o && o.v === 1 && Array.isArray(o.paras)) return o;
  } catch { /* битый JSON → плоский текст */ }
  return null;
}

/* ── публичный рендерер (безопасен: только текстовые ноды React) ── */
export function RichAbout({ rich, plain }) {
  const data = parseRich(rich);
  if (!data) {
    return <div className="rb-up__about-text">{plain}</div>;
  }
  return (
    <div className="rb-up__about-text rb-up__about-text--rich">
      {data.paras.map((p, pi) => (
        <div
          key={pi}
          style={p.align && p.align !== "left" ? { textAlign: p.align } : undefined}
        >
          {(!p.runs || p.runs.length === 0)
            ? <br />
            : p.runs.map((r, ri) => (
                <span key={ri} style={runStyle(r)}>{r.t}</span>
              ))}
        </div>
      ))}
    </div>
  );
}

/* ── JSON → HTML для загрузки в contenteditable ────────────────── */
function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function runCss(run) {
  const parts = [];
  if (run.b) parts.push("font-weight:800");
  if (run.i) parts.push("font-style:italic");
  const dec = [];
  if (run.u) dec.push("underline");
  if (run.s) dec.push("line-through");
  if (dec.length) parts.push(`text-decoration:${dec.join(" ")}`);
  if (run.c) parts.push(`color:${run.c}`);
  if (run.sz && SIZE_PX[run.sz]) parts.push(`font-size:${SIZE_PX[run.sz]}px`);
  if (run.f && FONT_STACKS[run.f]) parts.push(`font-family:${FONT_STACKS[run.f]}`);
  return parts.join(";");
}

function richToHtml(richStr, plain) {
  const data = parseRich(richStr);
  if (!data) {
    const lines = (plain || "").split("\n");
    return lines
      .map((l) => `<div>${l ? escapeHtml(l) : "<br>"}</div>`)
      .join("");
  }
  return data.paras
    .map((p) => {
      const align = p.align && p.align !== "left"
        ? ` style="text-align:${p.align}"` : "";
      const inner = (!p.runs || p.runs.length === 0)
        ? "<br>"
        : p.runs.map((r) => {
            const css = runCss(r);
            return css
              ? `<span style="${css}">${escapeHtml(r.t)}</span>`
              : escapeHtml(r.t);
          }).join("");
      return `<div${align}>${inner}</div>`;
    })
    .join("");
}

/* ── DOM contenteditable → JSON ────────────────────────────────── */
function normColor(v) {
  if (!v) return undefined;
  const m = v.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  if (m) {
    const h = (n) => Number(n).toString(16).padStart(2, "0");
    return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
  }
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
  }
  return undefined;
}

function familyToKey(family) {
  if (!family) return undefined;
  const f = family.toLowerCase();
  if (f.includes("georgia") || f.includes("times")) return "serif";
  if (f.includes("consolas") || f.includes("courier") || f.includes("mono")) return "mono";
  if (f.includes("comic")) return "rounded";
  if (f.includes("script") || f.includes("bradley")) return "hand";
  return undefined; // system-ui и прочее = обычный
}

function pxToSz(px) {
  const n = parseFloat(px);
  if (!Number.isFinite(n)) return undefined;
  let best; let bestD = Infinity;
  for (const [sz, p] of Object.entries(SIZE_PX)) {
    const d = Math.abs(p - n);
    if (d < bestD) { bestD = d; best = Number(sz); }
  }
  return best;
}

function domToRich(root) {
  const paras = [];
  let cur = { align: "left", runs: [] };
  const flush = () => { paras.push(cur); cur = { align: "left", runs: [] }; };

  const inline = (node, st) => {
    if (node.nodeType === 3) {
      if (node.nodeValue) cur.runs.push({ t: node.nodeValue, ...st });
      return;
    }
    if (node.nodeType !== 1) return;
    const el = node;
    const tag = el.tagName;
    if (tag === "BR") { flush(); return; }

    const next = { ...st };
    if (tag === "B" || tag === "STRONG") next.b = 1;
    if (tag === "I" || tag === "EM") next.i = 1;
    if (tag === "U") next.u = 1;
    if (tag === "S" || tag === "STRIKE" || tag === "DEL") next.s = 1;
    if (tag === "FONT") {
      const c = normColor(el.getAttribute("color"));
      if (c) next.c = c;
      const szA = parseInt(el.getAttribute("size"), 10);
      if (szA >= 1 && szA <= 7) next.sz = szA;
      const fk = familyToKey(el.getAttribute("face"));
      if (fk) next.f = fk; else if (el.getAttribute("face")) delete next.f;
    }
    const s = el.style;
    if (s) {
      const fw = s.fontWeight;
      if (fw === "bold" || parseInt(fw, 10) >= 600) next.b = 1;
      else if (fw === "normal" || (parseInt(fw, 10) > 0 && parseInt(fw, 10) < 600)) delete next.b;
      if (s.fontStyle === "italic") next.i = 1;
      else if (s.fontStyle === "normal") delete next.i;
      const td = s.textDecoration || s.textDecorationLine || "";
      if (td.includes("underline")) next.u = 1;
      if (td.includes("line-through")) next.s = 1;
      if (td.includes("none")) { delete next.u; delete next.s; }
      const c = normColor(s.color);
      if (c) next.c = c;
      if (s.fontSize) {
        const sz = pxToSz(s.fontSize);
        if (sz) next.sz = sz;
      }
      if (s.fontFamily) {
        const fk = familyToKey(s.fontFamily);
        if (fk) next.f = fk; else delete next.f;
      }
    }
    el.childNodes.forEach((c) => inline(c, next));
  };

  root.childNodes.forEach((n) => {
    if (n.nodeType === 1 && (n.tagName === "DIV" || n.tagName === "P")) {
      if (cur.runs.length) flush(); // инлайн-хвост первой строки
      const ta = n.style && n.style.textAlign;
      cur.align = ta === "center" || ta === "right" ? ta : "left";
      n.childNodes.forEach((c) => inline(c, {}));
      flush();
    } else {
      inline(n, {});
    }
  });
  if (cur.runs.length) flush();

  // хвостовые пустые абзацы не сохраняем
  while (paras.length && paras[paras.length - 1].runs.length === 0) paras.pop();

  const plain = paras
    .map((p) => p.runs.map((r) => r.t).join(""))
    .join("\n")
    .trim();

  if (!paras.length || !plain) return { rich: null, plain: "" };

  // {sz:3} = базовый размер, {f:'default'} = базовый шрифт — не храним
  const clean = paras.map((p) => ({
    ...(p.align !== "left" ? { align: p.align } : {}),
    runs: p.runs.map((r) => {
      const cr = { t: r.t };
      if (r.b) cr.b = 1;
      if (r.i) cr.i = 1;
      if (r.u) cr.u = 1;
      if (r.s) cr.s = 1;
      if (r.c) cr.c = r.c;
      if (r.sz && r.sz !== 3) cr.sz = r.sz;
      if (r.f && r.f !== "default") cr.f = r.f;
      return cr;
    }),
  }));
  return { rich: { v: 1, paras: clean }, plain };
}

/* ── Модалка-редактор ──────────────────────────────────────────── */
export function AboutEditorModal({ currentPlain, currentRich, onClose, onSaved }) {
  const edRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [len, setLen] = useState(0);
  // активные стили под курсором (для подсветки кнопок B/I/U/S)
  const [fmt, setFmt] = useState({ b: false, i: false, u: false, s: false });

  const recount = useCallback(() => {
    const el = edRef.current;
    if (!el) return;
    setLen(el.innerText.replace(/\n+$/, "").length);
  }, []);

  useEffect(() => {
    const el = edRef.current;
    if (!el) return;
    // Новые строки — <div> (одинаковая структура во всех браузерах)
    document.execCommand("defaultParagraphSeparator", false, "div");
    // Содержимое собираем САМИ из валидированного JSON (не сырой HTML)
    el.innerHTML = richToHtml(currentRich, currentPlain);
    recount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onSel = () => {
      try {
        setFmt({
          b: document.queryCommandState("bold"),
          i: document.queryCommandState("italic"),
          u: document.queryCommandState("underline"),
          s: document.queryCommandState("strikeThrough"),
        });
      } catch { /* не критично */ }
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);

  const exec = (cmd, val) => {
    const el = edRef.current;
    if (!el) return;
    el.focus();
    document.execCommand("styleWithCSS", false, false);
    document.execCommand(cmd, false, val);
    recount();
  };

  const save = async () => {
    if (busy) return;
    const el = edRef.current;
    if (!el) return;
    const { rich, plain } = domToRich(el);
    if (plain.length > ABOUT_MAX_LEN) {
      setError(`Слишком длинно — максимум ${ABOUT_MAX_LEN} символов`);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await putProfileAbout(plain, rich);
      onSaved(res.about, res.about_rich);
    } catch (e) {
      setError(e?.message || "Не удалось сохранить");
      setBusy(false);
    }
  };

  const left = ABOUT_MAX_LEN - len;
  return (
    <div className="rb-up__modal-overlay" onClick={onClose}>
      <div className="rb-up__modal rb-up__modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="rb-up__modal-head">
          <div className="rb-up__modal-title">
            <CabIcon name="pencil" size={18} />
            О себе
          </div>
          <button type="button" className="rb-up__modal-close" onClick={onClose} aria-label="Закрыть">
            <CabIcon name="close" size={16} />
          </button>
        </div>
        <div className="rb-up__modal-body">
          <p className="rb-up__modal-hint">
            Оформи описание как хочешь: жирный, цвет, размер, шрифт,
            выравнивание. До {ABOUT_MAX_LEN} символов текста. Чтобы убрать
            описание — сохрани пустым.
          </p>

          {/* ── тулбар ── */}
          <div className="rb-up__rte-toolbar">
            <button type="button" className={`rb-up__rte-btn ${fmt.b ? "is-on" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); exec("bold"); }}
                    title="Жирный"><b>Ж</b></button>
            <button type="button" className={`rb-up__rte-btn ${fmt.i ? "is-on" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); exec("italic"); }}
                    title="Курсив"><i>К</i></button>
            <button type="button" className={`rb-up__rte-btn ${fmt.u ? "is-on" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); exec("underline"); }}
                    title="Подчёркнутый"><u>Ч</u></button>
            <button type="button" className={`rb-up__rte-btn ${fmt.s ? "is-on" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); exec("strikeThrough"); }}
                    title="Зачёркнутый"><s>З</s></button>

            <span className="rb-up__rte-sep" />

            <select className="rb-up__rte-select" defaultValue="3"
                    title="Размер текста"
                    onChange={(e) => exec("fontSize", e.target.value)}>
              {Object.keys(SIZE_PX).map((sz) => (
                <option key={sz} value={sz}>{SIZE_TITLES[sz]}</option>
              ))}
            </select>

            <select className="rb-up__rte-select" defaultValue="default"
                    title="Шрифт"
                    onChange={(e) => exec("fontName",
                      e.target.value === "default" ? "system-ui" : FONT_STACKS[e.target.value])}>
              {Object.keys(FONT_TITLES).map((f) => (
                <option key={f} value={f}>{FONT_TITLES[f]}</option>
              ))}
            </select>

            <span className="rb-up__rte-sep" />

            <button type="button" className="rb-up__rte-btn"
                    onMouseDown={(e) => { e.preventDefault(); exec("justifyLeft"); }}
                    title="По левому краю">
              <svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M4 6h16M4 12h10M4 18h14" /></svg>
            </button>
            <button type="button" className="rb-up__rte-btn"
                    onMouseDown={(e) => { e.preventDefault(); exec("justifyCenter"); }}
                    title="По центру">
              <svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M4 6h16M7 12h10M5 18h14" /></svg>
            </button>
            <button type="button" className="rb-up__rte-btn"
                    onMouseDown={(e) => { e.preventDefault(); exec("justifyRight"); }}
                    title="По правому краю">
              <svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M4 6h16M10 12h10M6 18h14" /></svg>
            </button>

            <span className="rb-up__rte-sep" />

            <button type="button" className="rb-up__rte-btn rb-up__rte-btn--wide"
                    onMouseDown={(e) => { e.preventDefault(); exec("removeFormat"); }}
                    title="Очистить оформление выделенного">
              Очистить
            </button>
          </div>

          {/* палитра цвета текста + свой цвет */}
          <div className="rb-up__rte-palette">
            {PALETTE.map((c) => (
              <button key={c} type="button" className="rb-up__rte-swatch"
                      style={{ background: c }} title={c}
                      onMouseDown={(e) => { e.preventDefault(); exec("foreColor", c); }} />
            ))}
            <label className="rb-up__rte-custom" title="Свой цвет">
              <input type="color"
                     onChange={(e) => exec("foreColor", e.target.value)} />
              <CabIcon name="palette" size={15} />
            </label>
          </div>

          {/* ── сам редактор ── */}
          <div
            ref={edRef}
            className="rb-up__rte"
            contentEditable
            suppressContentEditableWarning
            spellCheck={true}
            onInput={recount}
            onPaste={(e) => {
              // вставка ТОЛЬКО текстом — чужой HTML в редактор не пускаем
              e.preventDefault();
              const t = e.clipboardData.getData("text/plain");
              if (t) document.execCommand("insertText", false, t);
            }}
          />

          <div className={`rb-up__about-counter ${left < 40 ? "is-low" : ""}`}>
            {left < 0 ? `лишних ${-left}` : `осталось ${left}`}
          </div>

          {error && <div className="rb-up__modal-error">{error}</div>}

          <div className="rb-up__modal-actions">
            <button
              type="button"
              className="rb-up__modal-btn rb-up__modal-btn--primary"
              disabled={busy || left < 0}
              onClick={save}
            >
              {busy ? "Сохраняем…" : "Сохранить"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
